/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/AccountMergeModal.tsx
 * PURPOSE: Merge two CRM accounts — survivor keeps records; duplicate is archived [2.2.1121]
 * PUBLIC_API: AccountMergeModal
 * SIDE_EFFECTS: POST /api/v1/crm/accounts/merge
 * AGENT_SAFETY: SAFE
 */

import { crmEntitySearch, crmMergeAccounts } from '@shared/lib/frontend/api/crmApi';
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, TextInput } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildCrmAccountDetailPath } from '../../../../routes/builders';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Account to keep (primary). */
  survivorId: string;
  survivorName: string;
  /** Pre-select duplicate when opening from duplicate-detection flows. */
  initialDuplicateId?: string;
  initialDuplicateName?: string;
  onMerged?: (survivorId: string) => void | Promise<void>;
};

export function AccountMergeModal({
  open,
  onClose,
  survivorId,
  survivorName,
  initialDuplicateId,
  initialDuplicateName,
  onMerged,
}: Props) {
  const [duplicateId, setDuplicateId] = useState('');
  const [duplicateLabel, setDuplicateLabel] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [hits, setHits] = useState<{ id: string; label: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDuplicateId(initialDuplicateId ?? '');
    setDuplicateLabel(initialDuplicateName ?? '');
    setSearchQ('');
    setHits([]);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  }, [open, initialDuplicateId, initialDuplicateName, survivorId]);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setHits([]);
      return;
    }
    setSearching(true);
    try {
      const res = await crmEntitySearch({ q: trimmed, types: 'accounts', limit: 8 });
      setHits(
        res.items
          .filter((h) => h.id !== survivorId)
          .map((h) => ({ id: h.id, label: h.label })),
      );
    } catch {
      setHits([]);
    } finally {
      setSearching(false);
    }
  }, [survivorId]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => void runSearch(searchQ), 280);
    return () => window.clearTimeout(t);
  }, [open, searchQ, runSearch]);

  const handleMerge = async () => {
    setError(null);
    setSuccess(null);
    if (!duplicateId.trim()) {
      setError('Select the duplicate account to merge in.');
      return;
    }
    if (duplicateId === survivorId) {
      setError('Choose a different account than the survivor.');
      return;
    }
    setSubmitting(true);
    try {
      const out = await crmMergeAccounts({ survivorId, duplicateId: duplicateId.trim() });
      const summary = [
        out.dealsMoved ? `${out.dealsMoved} deal${out.dealsMoved === 1 ? '' : 's'}` : null,
        out.contactsMoved ? `${out.contactsMoved} contact${out.contactsMoved === 1 ? '' : 's'}` : null,
        out.tasksMoved ? `${out.tasksMoved} task${out.tasksMoved === 1 ? '' : 's'}` : null,
        out.activitiesMoved
          ? `${out.activitiesMoved} activit${out.activitiesMoved === 1 ? 'y' : 'ies'}`
          : null,
      ]
        .filter(Boolean)
        .join(', ');
      setSuccess(
        summary
          ? `Merged "${out.duplicateName}" into "${out.survivorName}" (${summary} moved).`
          : `Merged "${out.duplicateName}" into "${out.survivorName}".`,
      );
      await onMerged?.(out.survivorId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Merge failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={open} onClose={onClose} size="md">
      <ModalHeader>Merge accounts</ModalHeader>
      <ModalBody className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Keep one account and move deals, contacts, and related activity from the duplicate. The
          duplicate account will be archived.
        </p>
        <div>
          <Label>Account to keep</Label>
          <p
            className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            data-testid="account-merge-survivor"
          >
            {survivorName}
          </p>
        </div>
        <div>
          <Label htmlFor="account-merge-search">Duplicate to merge in</Label>
          <TextInput
            id="account-merge-search"
            placeholder="Search accounts…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="mt-1"
            data-testid="account-merge-search"
          />
          {duplicateLabel ? (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-testid="account-merge-selected">
              Selected: <span className="font-medium">{duplicateLabel}</span>
            </p>
          ) : null}
          {searching ? (
            <p className="mt-2 text-xs text-gray-500">Searching…</p>
          ) : null}
          {hits.length > 0 ? (
            <ul
              className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600"
              data-testid="account-merge-hits"
            >
              {hits.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      setDuplicateId(h.id);
                      setDuplicateLabel(h.label);
                      setSearchQ(h.label);
                      setHits([]);
                    }}
                  >
                    {h.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="text-sm text-green-700 dark:text-green-400" role="status">
            {success}{' '}
            <Link
              to={buildCrmAccountDetailPath(survivorId)}
              className="font-medium underline"
              data-testid="account-merge-view-survivor"
            >
              View account →
            </Link>
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" type="button" onClick={onClose} data-testid="account-merge-cancel">
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success ? (
          <Button
            color="failure"
            type="button"
            disabled={submitting || !duplicateId}
            aria-busy={submitting}
            data-testid="account-merge-submit"
            onClick={() => void handleMerge()}
          >
            {submitting ? 'Merging…' : 'Merge accounts'}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}
